import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';
const REGISTRATION_API = process.env.REACT_APP_REGISTRATION_API_URL || 'http://localhost:8085';
const REVIEW_API = process.env.REACT_APP_REVIEW_API_URL || 'http://localhost:8082';

const MyReviews = () => {
  const [user, setUser] = useState(null);
  const [conferences, setConferences] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [visibleToReviewers, setVisibleToReviewers] = useState(true); // Default to true - allow viewing
  const [reviewForm, setReviewForm] = useState({
    score: '',
    comments: '',
    recommendation: 'ACCEPT'
  });

  useEffect(() => {
    // Get logged-in user from localStorage
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    }
  }, []);

  useEffect(() => {
    fetchActiveConferences();
  }, []);

  const fetchActiveConferences = async () => {
    try {
      console.log('Fetching conferences from:', `${CONFERENCE_API}/active`);
      const response = await fetch(`${CONFERENCE_API}/active`);
      console.log('Conference response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Conferences data:', data);
        setConferences(data);
      } else {
        console.error('Failed to fetch conferences:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };



  const fetchConferenceMetadata = async (conferenceId) => {
    try {
      const response = await fetch(`${CONFERENCE_API}/api/abstract-metadata/conference/${conferenceId}`);
      console.log('Fetching metadata from:', `${CONFERENCE_API}/api/abstract-metadata/conference/${conferenceId}`);
      if (response.ok) {
        const metadataArray = await response.json();
        console.log('Conference metadata response:', metadataArray);
        // The endpoint returns an array, get the first item
        const metadata = Array.isArray(metadataArray) && metadataArray.length > 0 ? metadataArray[0] : null;
        if (metadata && metadata.visibleToReviewers === false) {
          // Only restrict if explicitly set to false
          console.log('Blind review mode: visibleToReviewers is false');
          setVisibleToReviewers(false);
        } else {
          // Default to allowing paper viewing (normal review mode)
          console.log('Normal review mode: papers are viewable');
          setVisibleToReviewers(true);
        }
      } else {
        console.log('No metadata found or error, defaulting to visible');
        setVisibleToReviewers(true); // Default to showing if error
      }
    } catch (error) {
      console.error('Error fetching conference metadata:', error);
      setVisibleToReviewers(true); // Default to showing if error
    }
  };

  const fetchPapers = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Use review management service to get all papers assigned to this reviewer
      console.log('Fetching papers from:', `${REVIEW_API}/review/papers/reviewer/${user.id}`);
      const response = await fetch(`${REVIEW_API}/review/papers/reviewer/${user.id}`);
      console.log('Papers response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Papers data:', data);
        
        // Check review status for each paper
        const papersWithReviewStatus = await Promise.all(data.map(async (paper) => {
          try {
            const reviewResponse = await fetch(`${REVIEW_API}/review/reviews/paper/${paper.id}/reviewer/${user.id}`);
            console.log(`Review check for paper ${paper.id}: status ${reviewResponse.status}`);
            if (reviewResponse.ok) {
              const review = await reviewResponse.json();
              console.log(`Review data for paper ${paper.id}:`, review);
              const status = review.recommendation ? 'COMPLETED' : 'PENDING';
              console.log(`Setting reviewStatus to ${status} for paper ${paper.id}`);
              return {
                ...paper,
                reviewStatus: status,
                hasReview: !!review.recommendation
              };
            }
          } catch (error) {
            console.error('Error checking review status:', error);
          }
          return { ...paper, reviewStatus: 'PENDING', hasReview: false };
        }));
        
        setPapers(papersWithReviewStatus);
      } else {
        console.error('Failed to fetch papers:', response.status, response.statusText);
        setPapers([]);
        if (response.status === 0 || !response.status) {
          showAlert('Unable to connect to review service. Please ensure the service is running.', 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching papers:', error);
      setPapers([]);
      showAlert('Unable to connect to review service. Please contact administrator.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPapers();
    }
  }, [user]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleOpenReviewModal = async (paper, readonly = false) => {
    setSelectedPaper(paper);
    // Check if review already exists (including drafts without recommendation)
    try {
      const response = await fetch(`${REVIEW_API}/review/reviews/paper/${paper.id}/reviewer/${user.id}`);
      if (response.ok) {
        const existingReview = await response.json();
        console.log('Existing review found:', existingReview);
        setExistingReview(existingReview);
        // Load existing data even if no recommendation yet (draft review)
        setReviewForm({
          score: existingReview.rating || '',
          comments: existingReview.comments || '',
          recommendation: existingReview.recommendation || 'ACCEPT'
        });
        // Only make readonly if review has a recommendation (submitted)
        const shouldBeReadOnly = existingReview && existingReview.recommendation;
        setIsReadOnly(shouldBeReadOnly);
      } else if (response.status === 404) {
        // No review exists yet, start fresh
        console.log('No existing review, starting fresh');
        setExistingReview(null);
        setIsReadOnly(false);
        setReviewForm({ score: '', comments: '', recommendation: 'ACCEPT' });
      } else {
        // Other error, start fresh
        console.error('Error fetching review, status:', response.status);
        setExistingReview(null);
        setIsReadOnly(false);
        setReviewForm({ score: '', comments: '', recommendation: 'ACCEPT' });
      }
    } catch (error) {
      console.error('Error fetching existing review:', error);
      setExistingReview(null);
      setIsReadOnly(false);
      setReviewForm({ score: '', comments: '', recommendation: 'ACCEPT' });
    }
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setSelectedPaper(null);
    setExistingReview(null);
    setIsReadOnly(false);
    setReviewForm({ score: '', comments: '', recommendation: 'ACCEPT' });
  };

  const handleReviewInputChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!reviewForm.score || !reviewForm.comments) {
      showAlert('Please provide both score and comments', 'error');
      return;
    }

    const score = parseInt(reviewForm.score);
    if (score < 1 || score > 10) {
      showAlert('Score must be between 1 and 10', 'error');
      return;
    }

    setLoading(true);
    try {
      const reviewData = {
        paperId: selectedPaper.id,
        reviewerId: user.id,
        rating: score,
        comments: reviewForm.comments,
        recommendation: reviewForm.recommendation
      };

      console.log('Submitting review:', reviewData);
      
      // Use PUT if editing existing review, POST if creating new
      const method = existingReview ? 'PUT' : 'POST';
      const url = existingReview 
        ? `${REVIEW_API}/review/reviews/${existingReview.id}`
        : `${REVIEW_API}/review/reviews`;
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reviewData)
      });

      if (response.ok) {
        showAlert(existingReview ? 'Review updated successfully!' : 'Review submitted successfully!', 'success');
        handleCloseReviewModal();
        fetchPapers(selectedConferenceId); // Refresh the papers list
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to submit review', 'error');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      showAlert('Failed to submit review. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPaper = async (paper) => {
    // Construct file URL from registration service
    // Paper files are stored at: /registration/{registrationId}/file/paper
    if (paper.registrationId) {
      const fileUrl = `${REGISTRATION_API}/registration/${paper.registrationId}/file/paper`;
      console.log('Opening paper file:', fileUrl);
      window.open(fileUrl, '_blank');
    } else if (paper.fileUrl) {
      window.open(paper.fileUrl, '_blank');
    } else {
      showAlert('Paper file not available. Registration ID is missing.', 'error');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'SUBMITTED': '#17a2b8',
      'UNDER_REVIEW': '#ffc107',
      'ACCEPTED': '#28a745',
      'REJECTED': '#dc3545',
      'MINOR_REVISION': '#fd7e14',
      'MAJOR_REVISION': '#e83e8c'
    };
    return colors[status] || '#6c757d';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2>My Reviews</h2>
        {user && (
          <p style={{ color: '#666', marginTop: '8px' }}>
            Welcome, {user.fullName} ({user.role})
          </p>
        )}
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading papers...</div>}

      {!loading && papers.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>No Papers Assigned</h3>
          <p>You don't have any papers assigned for review yet.</p>
        </div>
      )}

      {!loading && papers.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Paper ID</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Title</th>
                {visibleToReviewers && (
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Author</th>
                )}
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Conference</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Track</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Presentation Mode</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Submitted</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Review Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {papers.map(paper => (
                <tr key={paper.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{paper.id}</td>
                  <td style={{ padding: '12px' }}>{paper.title}</td>
                  {visibleToReviewers && (
                    <td style={{ padding: '12px' }}>{paper.authorName}</td>
                  )}
                  <td style={{ padding: '12px' }}>{paper.conference || '-'}</td>
                  <td style={{ padding: '12px' }}>{paper.trackName || '-'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {paper.presentationMode ? (
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: '#e7f3ff',
                        color: '#0d47a1',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {paper.presentationMode}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>{formatDate(paper.submissionDate)}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: getStatusColor(paper.status),
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {paper.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: paper.reviewStatus === 'COMPLETED' ? '#28a745' : '#ffc107',
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {paper.reviewStatus || 'PENDING'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleViewPaper(paper)}
                      title="View paper"
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '8px',
                        fontSize: '12px'
                      }}
                    >
                      📄 View Paper
                    </button>
                    <button
                      onClick={() => handleOpenReviewModal(paper, paper.reviewStatus === 'COMPLETED')}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: paper.reviewStatus === 'COMPLETED' ? '#17a2b8' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {paper.reviewStatus === 'COMPLETED' ? 'View Review' : 'Submit Review'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedPaper && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>        onKeyDown={(e) => {
          // Prevent Ctrl+C, Ctrl+X, Ctrl+A, Ctrl+P
          if (e.ctrlKey || e.metaKey) {
            if (['c', 'x', 'a', 'p'].includes(e.key.toLowerCase())) {
              e.preventDefault();
              return false;
            }
          }
        }}          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px' }}>
              <h2 style={{ margin: '0 0 10px 0', color: '#333' }}>
                {isReadOnly ? 'View Review' : (selectedPaper.reviewStatus === 'COMPLETED' ? 'Edit Review' : 'Submit Review')}
              </h2>
              <h3 style={{ margin: '5px 0', color: '#666', fontSize: '16px', fontWeight: 'normal' }}>
                {selectedPaper.title}
              </h3>
              <p style={{ margin: '5px 0', color: '#888', fontSize: '14px' }}>
                Author: {selectedPaper.authorName} | Track: {selectedPaper.trackName}
              </p>
              
              <div style={{ 
                marginTop: '15px', 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px',
                borderLeft: '4px solid #007bff'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '14px', fontWeight: 'bold' }}>
                  Abstract:
                </h4>
                {selectedPaper.abstractText ? (
                  <div
                    style={{ 
                      margin: 0, 
                      color: '#555', 
                      fontSize: '14px', 
                      lineHeight: '1.6', 
                      whiteSpace: 'pre-wrap',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      position: 'relative'
                    }}
                    onContextMenu={(e) => { e.preventDefault(); return false; }}
                    onCopy={(e) => { e.preventDefault(); return false; }}
                    onCut={(e) => { e.preventDefault(); return false; }}
                    onDragStart={(e) => { e.preventDefault(); return false; }}
                    onSelectStart={(e) => { e.preventDefault(); return false; }}
                  >
                    {selectedPaper.abstractText}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      pointerEvents: 'none',
                      opacity: 0.03,
                      fontSize: '48px',
                      fontWeight: 'bold',
                      color: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transform: 'rotate(-45deg)',
                      whiteSpace: 'nowrap'
                    }}>
                      CONFIDENTIAL
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: 0, color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                    No abstract provided for this paper.
                  </p>
                )}
              </div>
              
              {isReadOnly && (
                <div style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}>
                  ✓ Review completed and submitted
                </div>
              )}
            </div>

            <form onSubmit={handleSubmitReview}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Score (1-10) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  name="score"
                  min="1"
                  max="10"
                  value={reviewForm.score}
                  onChange={handleReviewInputChange}
                  required
                  disabled={isReadOnly}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                    cursor: isReadOnly ? 'not-allowed' : 'text'
                  }}
                  placeholder="Enter score between 1 and 10"
                />
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <span>1 = Poor</span>
                  <span>5 = Average</span>
                  <span>10 = Excellent</span>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Recommendation <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="recommendation"
                  value={reviewForm.recommendation}
                  onChange={handleReviewInputChange}
                  required
                  disabled={isReadOnly}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                    cursor: isReadOnly ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="ACCEPT">Accept</option>
                  <option value="REJECT">Reject</option>
                  <option value="MINOR_REVISION">Minor Revision</option>
                  <option value="MAJOR_REVISION">Major Revision</option>
                </select>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Comments <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  name="comments"
                  value={reviewForm.comments}
                  onChange={handleReviewInputChange}
                  required
                  disabled={isReadOnly}
                  rows="8"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                    cursor: isReadOnly ? 'not-allowed' : 'text'
                  }}
                  placeholder="Provide detailed feedback about the paper's strengths, weaknesses, and suggestions for improvement..."
                />
                {!isReadOnly && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Please provide constructive feedback to help the author improve their work.
                  </p>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end',
                borderTop: '1px solid #f0f0f0',
                paddingTop: '20px'
              }}>
                <button
                  type="button"
                  onClick={handleCloseReviewModal}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!isReadOnly && (
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {loading ? 'Submitting...' : 'Submit Review'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyReviews;
